import React, { useId, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import {
  Code2, Layers, Zap, ArrowRight, CheckCircle2,
  AlertCircle, Loader, ChevronDown, Mail, Clock,
} from 'lucide-react';
import Navbar from './Navbar';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { emailService } from '../utils/emailService';
import { events } from '../utils/analytics';
import { PageSEO } from '../utils/seo';
import { SERVIZI } from './Servizi';

/* ─── Reveal ─── */
function Reveal({ children, delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 28 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.75, ease: [0.16, 1, 0.3, 1], delay }}
    >
      {children}
    </motion.div>
  );
}

/* ─── Input con focus colorato ─── */
function FormField({ label, children, required }) {
  const id = useId();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      <label htmlFor={id} style={{
        fontFamily: 'var(--font-mono)', fontSize: '0.78rem',
        color: 'var(--color-accent-mid)', letterSpacing: '0.1em',
        textTransform: 'uppercase',
      }}>
        {label}{required && <span style={{ color: 'var(--color-pink)', marginLeft: 3 }}>*</span>}
      </label>
      {React.cloneElement(children, { id })}
    </div>
  );
}

const inputBase = {
  width: '100%',
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.09)',
  borderRadius: 'var(--radius-md)',
  padding: '13px 16px',
  fontFamily: 'var(--font-body)',
  fontSize: '1rem',
  color: 'var(--color-text)',
  outline: 'none',
  transition: 'border-color 0.2s, box-shadow 0.2s',
  WebkitAppearance: 'none',
};

function Input({ onFocus, onBlur, ...props }) {
  const [focused, setFocused] = useState(false);
  return (
    <input
      {...props}
      style={{
        ...inputBase,
        borderColor: focused ? '#7c3aed' : 'rgba(255,255,255,0.09)',
        boxShadow: focused ? '0 0 0 3px rgba(124,58,237,0.18)' : 'none',
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    />
  );
}

function Textarea({ ...props }) {
  const [focused, setFocused] = useState(false);
  return (
    <textarea
      {...props}
      style={{
        ...inputBase,
        minHeight: 130,
        resize: 'vertical',
        borderColor: focused ? '#7c3aed' : 'rgba(255,255,255,0.09)',
        boxShadow: focused ? '0 0 0 3px rgba(124,58,237,0.18)' : 'none',
        lineHeight: 1.6,
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    />
  );
}

function Select({ children, ...props }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <select
        {...props}
        style={{
          ...inputBase,
          paddingRight: '40px',
          cursor: 'pointer',
          borderColor: focused ? '#7c3aed' : 'rgba(255,255,255,0.09)',
          boxShadow: focused ? '0 0 0 3px rgba(124,58,237,0.18)' : 'none',
        }}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      >
        {children}
      </select>
      <ChevronDown size={15} style={{
        position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)',
        color: 'var(--color-muted)', pointerEvents: 'none',
      }} />
    </div>
  );
}

/* ─── Dati "cosa offro" ─── */
const OFFRO = [
  {
    icon: Code2,
    titolo: 'Sviluppo Full-Stack',
    color: '#7c3aed',
    testo: 'Web app, mobile (React Native), API e backend. Costruisco prodotti completi end-to-end.',
  },
  {
    icon: Layers,
    titolo: 'Architettura SaaS',
    color: '#f472b6',
    testo: 'Progetto sistemi scalabili pensati per crescere: autenticazione, billing, multi-tenancy.',
  },
  {
    icon: Zap,
    titolo: 'CTO as a Service',
    color: '#22d3ee',
    testo: 'Consulenza tecnica, revisione architettura, MVP in settimane. Senza assumere un team.',
  },
];

/* ════════════════════════════════════════
   COLLABORA PAGE
════════════════════════════════════════ */
export default function CollaboraPage() {
  // Dalle pagine /servizi/<slug> si arriva con ?servizio=<slug>: il tipo è già selezionato
  const [params] = useSearchParams();
  const servizio = SERVIZI.find(s => s.slug === params.get('servizio'));
  const [form, setForm] = useState({
    nome: '', email: '', tipo: servizio ? servizio.name : '', descrizione: '', budget: '', tempistiche: '',
  });
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMsg('');

    try {
      /* 1. Salva su Firestore */
      await addDoc(collection(db, 'richieste'), {
        ...form,
        createdAt: serverTimestamp(),
        letto: false,
      });

      /* 2. Email di notifica */
      await emailService.sendEmail({
        subject: `Nuova richiesta collaborazione — ${form.tipo || 'Generico'}`,
        contact_name: form.nome,
        contact_email: form.email,
        request_type: form.tipo || 'Non specificato',
        message: `${form.descrizione}\n\nBudget: ${form.budget}\nTempistiche: ${form.tempistiche}`,
        reply_to: form.email,
      });

      events.formSubmit({ tipo: form.tipo, budget: form.budget, tempistiche: form.tempistiche });
      setStatus('success');
    } catch (err) {
      console.error(err);
      setErrorMsg('Errore durante l\'invio. Riprova o scrivimi a vmdpproject@gmail.com');
      setStatus('error');
    }
  };

  return (
    <>
      <PageSEO
        title="Collabora — Preventivo sviluppo app, SaaS e web app"
        description="Hai un'idea o un progetto da sviluppare? Contatta VMDProject per sviluppare la tua app mobile, SaaS o web app. Prima call gratuita, risposta entro 24h."
        canonical="/collabora"
      />
      <Navbar />
      <motion.main
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0, y: -16 }}
        transition={{ duration: 0.4 }}
        style={{ background: 'var(--color-bg)', minHeight: '100vh' }}
      >

        {/* ══════════ HERO ══════════ */}
        <section style={{
          minHeight: '52vh',
          display: 'flex', alignItems: 'center',
          paddingTop: '68px',
          position: 'relative', overflow: 'hidden',
        }}>
          {/* Blob decorativi */}
          <div className="blob" style={{
            width: 700, height: 600,
            background: 'radial-gradient(circle, rgba(124,58,237,0.18) 0%, transparent 65%)',
            top: '-20%', left: '-10%', animationDuration: '18s',
          }} />
          <div className="blob" style={{
            width: 500, height: 450,
            background: 'radial-gradient(circle, rgba(244,114,182,0.12) 0%, transparent 65%)',
            bottom: '0%', right: '-8%', animationDuration: '22s', animationDelay: '-9s',
          }} />

          <div className="container" style={{ position: 'relative', zIndex: 1, paddingTop: '3rem', paddingBottom: '3rem' }}>
            <motion.div
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.75, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
            >
              <span style={{
                fontFamily: 'var(--font-mono)', fontSize: '0.85rem',
                color: 'var(--color-accent-mid)', letterSpacing: '0.12em',
                textTransform: 'uppercase', display: 'block', marginBottom: '1.25rem',
              }}>{'// collabora'}</span>

              <h1 style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'clamp(2.6rem, 6vw, 4.2rem)',
                fontWeight: 700, lineHeight: 1.1, letterSpacing: '-0.03em',
                maxWidth: '16ch',
              }}>
                Hai un'idea?{' '}
                <span style={{
                  background: 'var(--gradient-text)',
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                  fontWeight: 800,
                }}>
                  Costruiamola insieme.
                </span>
              </h1>

              <p style={{
                marginTop: '1.5rem',
                fontSize: 'clamp(1.05rem, 2vw, 1.25rem)',
                color: 'var(--color-text-2)', fontWeight: 300,
                maxWidth: '52ch', lineHeight: 1.75,
              }}>
                Raccontami il tuo progetto — anche se è ancora un'idea grezza.
                Valutiamo insieme cosa costruire e come farlo nel modo giusto.
              </p>

              {/* Info pills */}
              <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem', flexWrap: 'wrap' }}>
                {[
                  { icon: Clock, text: 'Risposta entro 48h' },
                  { icon: Mail,  text: 'vmdpproject@gmail.com' },
                ].map(({ icon: Icon, text }) => (
                  <div key={text} style={{
                    display: 'inline-flex', alignItems: 'center', gap: '8px',
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: '100px', padding: '7px 16px',
                  }}>
                    <Icon size={14} color="var(--color-accent-mid)" />
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.82rem', color: 'var(--color-muted)' }}>
                      {text}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* ══════════ COSA OFFRO ══════════ */}
        <section className="section" style={{ borderTop: '1px solid var(--color-border)' }}>
          <div className="container">
            <Reveal>
              <span style={{
                fontFamily: 'var(--font-mono)', fontSize: '0.85rem',
                color: 'var(--color-accent-mid)', letterSpacing: '0.12em',
                textTransform: 'uppercase', display: 'block', marginBottom: '1rem',
              }}>{'// cosa offro'}</span>
              <h2 style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'clamp(2rem, 4vw, 2.8rem)',
                lineHeight: 1.15, letterSpacing: '-0.03em', maxWidth: '22ch',
              }}>
                Dal problema al{' '}
                <span style={{
                  background: 'var(--gradient-text)',
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                  fontWeight: 800,
                }}>
                  prodotto finito
                </span>
              </h2>
            </Reveal>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(270px, 1fr))', gap: '1.5rem', marginTop: '3rem' }}>
              {OFFRO.map(({ icon: Icon, titolo, color, testo }, i) => (
                <Reveal key={titolo} delay={i * 0.1}>
                  <div
                    onMouseEnter={e => {
                      e.currentTarget.style.border = `1px solid ${color}55`;
                      e.currentTarget.style.boxShadow = `0 20px 60px ${color}18, inset 0 1px 0 ${color}15`;
                      e.currentTarget.style.transform = 'translateY(-6px)';
                      e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.border = `1px solid ${color}28`;
                      e.currentTarget.style.boxShadow = '0 4px 24px rgba(0,0,0,0.2)';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.background = 'rgba(255,255,255,0.025)';
                    }}
                    style={{
                      background: 'rgba(255,255,255,0.025)',
                      border: `1px solid ${color}28`,
                      backdropFilter: 'blur(16px)',
                      borderRadius: 'var(--radius-lg)',
                      padding: '2rem',
                      position: 'relative', overflow: 'hidden',
                      transition: 'all 0.32s cubic-bezier(0.4,0,0.2,1)',
                      boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
                    }}
                  >
                    <div style={{
                      position: 'absolute', top: 0, right: 0, width: 180, height: 180,
                      background: `radial-gradient(circle at top right, ${color}20, transparent 65%)`,
                      pointerEvents: 'none',
                    }} />
                    <div style={{
                      position: 'absolute', bottom: 0, left: 0, right: 0, height: '2px',
                      background: `linear-gradient(90deg, ${color}70, ${color}00)`,
                      pointerEvents: 'none',
                    }} />
                    <div style={{
                      width: 52, height: 52, borderRadius: '14px',
                      background: `${color}15`, border: `1px solid ${color}35`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      marginBottom: '1.4rem',
                      boxShadow: `0 0 20px ${color}25`,
                    }}>
                      <Icon size={22} color={color} />
                    </div>
                    <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '1.2rem', marginBottom: '0.6rem' }}>
                      {titolo}
                    </h3>
                    <p style={{ color: 'var(--color-muted)', fontSize: '1rem', lineHeight: 1.7 }}>{testo}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ══════════ FORM ══════════ */}
        <section className="section" style={{ borderTop: '1px solid var(--color-border)', position: 'relative', overflow: 'hidden' }}>
          <div className="blob" style={{
            width: 600, height: 500,
            background: 'radial-gradient(circle, rgba(124,58,237,0.14) 0%, transparent 70%)',
            top: '10%', right: '-10%', animationDuration: '20s',
          }} />

          <div className="container" style={{ position: 'relative', zIndex: 1 }}>
            <Reveal>
              <span style={{
                fontFamily: 'var(--font-mono)', fontSize: '0.85rem',
                color: 'var(--color-accent-mid)', letterSpacing: '0.12em',
                textTransform: 'uppercase', display: 'block', marginBottom: '1rem',
              }}>{'// richiesta'}</span>
              <h2 style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'clamp(2rem, 4vw, 2.8rem)',
                lineHeight: 1.15, letterSpacing: '-0.03em', marginBottom: '2.5rem',
              }}>
                Raccontami{' '}
                <span style={{
                  background: 'var(--gradient-text)',
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                  fontWeight: 800,
                }}>
                  il tuo progetto
                </span>
              </h2>
            </Reveal>

            {/* Card form */}
            <Reveal delay={0.1}>
              <div style={{
                background: 'rgba(255,255,255,0.025)',
                border: '1px solid rgba(255,255,255,0.08)',
                backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
                borderRadius: 'var(--radius-xl)',
                padding: 'clamp(2rem, 5vw, 3.5rem)',
                position: 'relative', overflow: 'hidden',
                maxWidth: '780px',
              }}>
                {/* Glow interno superiore */}
                <div style={{
                  position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
                  width: '60%', height: '180px',
                  background: 'radial-gradient(ellipse at 50% 0%, rgba(124,58,237,0.14), transparent 70%)',
                  pointerEvents: 'none',
                }} />

                <AnimatePresence mode="wait">
                  {status === 'success' ? (
                    /* ── Stato SUCCESS ── */
                    <motion.div
                      key="success"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                      style={{ textAlign: 'center', padding: '3rem 1rem', position: 'relative', zIndex: 1 }}
                    >
                      <div style={{
                        width: 72, height: 72, borderRadius: '50%',
                        background: 'rgba(52,211,153,0.1)',
                        border: '1px solid rgba(52,211,153,0.3)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        margin: '0 auto 1.75rem',
                        boxShadow: '0 0 32px rgba(52,211,153,0.2)',
                      }}>
                        <CheckCircle2 size={32} color="#34d399" />
                      </div>
                      <h3 style={{
                        fontFamily: 'var(--font-display)', fontWeight: 700,
                        fontSize: 'clamp(1.6rem, 3vw, 2.2rem)',
                        marginBottom: '0.75rem', letterSpacing: '-0.02em',
                      }}>
                        Messaggio ricevuto!
                      </h3>
                      <p style={{ color: 'var(--color-text-2)', fontSize: '1.1rem', fontWeight: 300, maxWidth: '40ch', margin: '0 auto 2.5rem', lineHeight: 1.7 }}>
                        Ti rispondo entro 48 ore. Nel frattempo dai un'occhiata ai progetti.
                      </p>
                      <Link to="/progetti" className="btn-primary">
                        Esplora i Progetti <ArrowRight size={15} />
                      </Link>
                    </motion.div>
                  ) : (
                    /* ── Form ── */
                    <motion.form
                      key="form"
                      onSubmit={handleSubmit}
                      style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', position: 'relative', zIndex: 1 }}
                    >
                      {/* Errore globale */}
                      <AnimatePresence>
                        {status === 'error' && (
                          <motion.div
                            initial={{ opacity: 0, y: -8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '10px',
                              background: 'rgba(244,114,182,0.08)',
                              border: '1px solid rgba(244,114,182,0.3)',
                              borderRadius: 'var(--radius-md)',
                              padding: '12px 16px',
                              color: '#f472b6', fontSize: '0.9rem',
                            }}
                          >
                            <AlertCircle size={16} style={{ flexShrink: 0 }} />
                            {errorMsg}
                          </motion.div>
                        )}
                      </AnimatePresence>

                      {/* Riga 1: Nome + Email */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.25rem' }}>
                        <FormField label="Nome e Cognome" required>
                          <Input
                            type="text" placeholder="Marco Rossi"
                            value={form.nome} onChange={set('nome')}
                            required autoComplete="name"
                          />
                        </FormField>
                        <FormField label="Email" required>
                          <Input
                            type="email" placeholder="marco@esempio.com"
                            value={form.email} onChange={set('email')}
                            required autoComplete="email"
                          />
                        </FormField>
                      </div>

                      {/* Tipo richiesta */}
                      <FormField label="Tipo di richiesta" required>
                        <Select value={form.tipo} onChange={set('tipo')} required>
                          <option value="" disabled style={{ background: '#0d0d18' }}>Seleziona...</option>
                          {SERVIZI.map(s => (
                            <option key={s.slug} value={s.name} style={{ background: '#0d0d18' }}>{s.name}</option>
                          ))}
                          <option value="Nuovo progetto" style={{ background: '#0d0d18' }}>Nuovo progetto</option>
                          <option value="Collaborazione" style={{ background: '#0d0d18' }}>Collaborazione</option>
                          <option value="CTO as a Service" style={{ background: '#0d0d18' }}>CTO as a Service</option>
                          <option value="Altro" style={{ background: '#0d0d18' }}>Altro</option>
                        </Select>
                      </FormField>

                      {/* Descrizione */}
                      <FormField label="Descrivi il tuo progetto" required>
                        <Textarea
                          placeholder="Anche un'idea grezza va benissimo. Di cosa hai bisogno, qual è il problema che vuoi risolvere, a chi si rivolge..."
                          value={form.descrizione} onChange={set('descrizione')}
                          required
                        />
                      </FormField>

                      {/* Budget + Tempistiche */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.25rem' }}>
                        <FormField label="Budget indicativo">
                          <Select value={form.budget} onChange={set('budget')}>
                            <option value="" disabled style={{ background: '#0d0d18' }}>Seleziona...</option>
                            <option value="< 1.000€" style={{ background: '#0d0d18' }}>Meno di 1.000€</option>
                            <option value="1.000 – 5.000€" style={{ background: '#0d0d18' }}>1.000 – 5.000€</option>
                            <option value="5.000 – 15.000€" style={{ background: '#0d0d18' }}>5.000 – 15.000€</option>
                            <option value="> 15.000€" style={{ background: '#0d0d18' }}>Oltre 15.000€</option>
                            <option value="Da definire" style={{ background: '#0d0d18' }}>Da definire insieme</option>
                          </Select>
                        </FormField>
                        <FormField label="Tempistiche">
                          <Select value={form.tempistiche} onChange={set('tempistiche')}>
                            <option value="" disabled style={{ background: '#0d0d18' }}>Seleziona...</option>
                            <option value="Urgente (< 1 mese)" style={{ background: '#0d0d18' }}>Urgente (meno di 1 mese)</option>
                            <option value="1 – 3 mesi" style={{ background: '#0d0d18' }}>1 – 3 mesi</option>
                            <option value="3 – 6 mesi" style={{ background: '#0d0d18' }}>3 – 6 mesi</option>
                            <option value="Flessibile" style={{ background: '#0d0d18' }}>Flessibile</option>
                          </Select>
                        </FormField>
                      </div>

                      {/* Submit */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                        <button
                          type="submit"
                          disabled={status === 'loading'}
                          className="btn-primary"
                          style={{ opacity: status === 'loading' ? 0.7 : 1, cursor: status === 'loading' ? 'not-allowed' : 'pointer' }}
                        >
                          {status === 'loading' ? (
                            <>
                              <Loader size={15} style={{ animation: 'spin 1s linear infinite' }} />
                              Invio in corso…
                            </>
                          ) : (
                            <>
                              Invia richiesta <ArrowRight size={15} />
                            </>
                          )}
                        </button>
                        <p style={{ fontSize: '0.85rem', color: 'var(--color-muted)', fontFamily: 'var(--font-mono)' }}>
                          Ti rispondo entro 48 ore
                        </p>
                      </div>
                    </motion.form>
                  )}
                </AnimatePresence>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ─── Footer ─── */}
        <footer style={{ borderTop: '1px solid var(--color-border)', padding: '2rem 0' }}>
          <div className="container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
            <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '0.95rem', letterSpacing: '-0.02em' }}>
              VMDProject
            </span>
            <div style={{ display: 'flex', gap: '1.5rem' }}>
              {[{ label: 'Privacy', href: '/policy' }, { label: 'Termini', href: '/terms-and-conditions' }].map(({ label, href }) => (
                <Link key={href} to={href}
                  style={{ fontSize: '0.9rem', color: 'var(--color-muted)', transition: 'color 0.3s' }}
                  onMouseEnter={e => e.target.style.color = 'var(--color-text)'}
                  onMouseLeave={e => e.target.style.color = 'var(--color-muted)'}
                >{label}</Link>
              ))}
            </div>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.82rem', color: 'var(--color-muted)' }}>
              © {new Date().getFullYear()} VMDProject · P.IVA 10990261215
            </span>
          </div>
        </footer>

      </motion.main>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        select option { background: #0d0d18; color: #f0f0f8; }
      `}</style>
    </>
  );
}
